// @flow
import CONST from 'app/common/lib/Const';
import urlUtil from 'app/common/lib/urlUtil';
import type { QueryStateParams, SearchScope } from 'app/packages/pinterest-flow-types/search';

export const QUERY_TERM_STATE_TYPE = {
  AUTO_COMPLETE_DEFAULT: 'query',
  AUTO_COMPLETE_HASHTAG: 'hashtag',
  AUTO_COMPLETE_RECENT_SEARCH: 'recent_query',
  AUTO_COMPLETE_RECOMMENDED_SEARCH: 'recommended_query',
  AUTO_COMPLETE_TRENDING: 'trending',
  TYPO_AUTO_CORRECT_ORIGINAL: 'typo_auto_original',
  USER_INPUT: 'typed',
  SEARCH_GUIDE: 'add_refine',
};

/**
 * Normalizes a search query (all whitespace normalized to one space)
 *
 * @param {string} query Search query to normalize
 * @returns {string} The query with extra whitespace stripped
 */
export function normalizeSearchQuery(query: string) {
  return (query || '').trim().replace(/\s+/g, ' ');
}

/**
 * Converts a query state param object into a string.
 *
 * @param {string} type Query type classifier (one of QUERY_TERM_STATE_TYPE)
 * @param {string} term Query term (one word usually)
 * @param {index} index Index of term in whole query
 * @returns {string} The parameters interpolated, with additional information added depending on type.
 */
export function constructSingleQueryStateParam({ type, term = '', index }: QueryStateParams) {
  switch (type) {
    case QUERY_TERM_STATE_TYPE.USER_INPUT:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_HASHTAG:
    case QUERY_TERM_STATE_TYPE.TYPO_AUTO_CORRECT_ORIGINAL:
      return `${term}|${type}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_DEFAULT:
      return `${term}|autocomplete|${index}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECENT_SEARCH:
      return `${term}|recentsearch|${index}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_TRENDING:
      return `${term}|trending|${index}`;
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECOMMENDED_SEARCH:
      return `${term}|recommended|${index}`;
    case QUERY_TERM_STATE_TYPE.SEARCH_GUIDE:
      return `${term}|guide|word|${index}`;
    default:
      return '';
  }
}

/**
 * Converts a query state param object into a string.
 *
 * @param {QueryStateParams[]} queryStates array of query states (usually previously constructed)
 * @returns {string} queryStates and guideAdded string interpolated, with & in front to append to URL.
 */
export function constructQueryStatesParams({
  queryStates,
}: {
  queryStates: Array<QueryStateParams>,
}) {
  if (queryStates.length === 0) {
    return '';
  }
  return (
    '&' +
    urlUtil.getQueryStringFromObject({
      term_meta: queryStates.map((s) => constructSingleQueryStateParam(s)),
    })
  );
}

/**
 * Converts a query state param object into a string.
 *
 * @param {string} query Search query that is going to be broken down into terms
 * @returns {string[]} Array of search terms. example: 'red merle aussie' => ['red', 'merle', 'aussie']
 */
export function queryToTerms(query: string): string[] {
  if (query) {
    const terms = query.trim().split(/\s+/);
    const conjunction = terms.find((term) =>
      CONST.SEARCH_FILTER_CONJUNCTIONS.includes(term.toLowerCase()),
    );

    if (conjunction) {
      return [query];
    } else {
      return terms;
    }
  }

  return [];
}

/**
 * Converts a query (with type and index) into its query states.
 *
 * @param {string} type Type of search query (hashtag, autocomplete, etc.)
 * @param {string} query Search query
 * @param {number} index Index of the query in whatever list it appeared
 * @returns {QueryStateParam[]} Array of query state params. Basically breaks the query down into terms and makes each
 *                              a query state param.
 */
export function getNewQueryStates({
  type,
  query = '',
  index,
}: QueryStateParams): QueryStateParams[] {
  switch (type) {
    case QUERY_TERM_STATE_TYPE.USER_INPUT:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_HASHTAG:
    case QUERY_TERM_STATE_TYPE.TYPO_AUTO_CORRECT_ORIGINAL:
      return queryToTerms(query).map((term) => ({
        index,
        term,
        type,
      }));

    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECENT_SEARCH:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_RECOMMENDED_SEARCH:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_TRENDING:
    case QUERY_TERM_STATE_TYPE.AUTO_COMPLETE_DEFAULT:
      return queryToTerms(query).map((term) => ({
        term,
        type,
        index, // index is resultIndex in the list
      }));

    default:
      return [];
  }
}

/**
 * Given the queryStates and other query information, construct the search URL
 *
 * @param {Object} queryData
 *        {string} query The search query
 *        {string} rs Referring source of the query (rs = recent search, typed, etc)
 *        {string} scope (Optional) Scope of query (pins, buyable_pins, my_pins, users, boards)
 *        {string[]} termMeta Array of the query states in string format that will get appended to end
 *
 *        {Object} guideAdded (Optional) If a guide was just added, will contain term and index
 *        {string} filters (Optional) If there are filters to consider
 * @returns {string} The query with extra whitespace stripped
 */
export function buildQueryDataUrl(queryData: {
  query: string,
  rs: string,
  termMeta: string[],
  scope?: SearchScope,
  guideAdded?: { term: string, index: number },
  filters?: string,
  user?: string,
}): string {
  const { query, rs, scope, guideAdded, filters, termMeta, user } = queryData;

  if (!query) {
    return '/';
  }

  const paramsMap: {
    q: string,
    rs: string,
    term_meta: string[],
    filters?: string,
    add_refine?: string,
    user?: string,
  } = {
    q: query,
    rs,
    term_meta: termMeta,
  };

  if (filters) {
    paramsMap.filters = filters;
  }

  if (user) {
    paramsMap.user = user;
  }

  if (guideAdded && guideAdded.term) {
    paramsMap.add_refine = `${guideAdded.term}|guide|word|${guideAdded.index}`;
  }

  if (window.location.pathname.startsWith('/explore')) {
    return `/search/pins/?${urlUtil.getQueryStringFromObject(paramsMap)}`;
  }

  // Final check of allowable scopes to prevent going to urls that can't be handled by:
  // webapp/app/mobile/routes/SearchResultsPage.js
  let finalScope = scope;
  const allowedSearchScopes: Array<SearchScope> = [
    'pins',
    'buyable_pins',
    'my_pins',
    'boards',
    'users',
  ];
  if (!allowedSearchScopes.includes(finalScope)) {
    // Assume scope is pins
    finalScope = 'pins';
  }

  return finalScope
    ? `/search/${finalScope}/?${urlUtil.getQueryStringFromObject(paramsMap)}`
    : `${window.location.pathname}?${urlUtil.getQueryStringFromObject(paramsMap)}`;
}

/**
 * Appends the guide added query param to the url
 * NOTE: Assumes that the URL is already a search URL
 *
 * @param {string} query New query with the added term
 * @param {string} term Search term that is added
 * @param {number} index Guide index that was selected
 * @param {string} rs Referring source for this query
 * @returns {string} The query with extra whitespace stripped
 */
export function buildUrlWithGuideAdded(query: string, term: string, index: number, rs: string) {
  const urlParams = urlUtil.getQueryStringParams();

  if (urlParams.add_refine) {
    if (urlParams['term_meta[]']) {
      urlParams['term_meta[]'].push(urlParams.add_refine);
    } else {
      urlParams['term_meta[]'] = [urlParams.add_refine];
    }
  }

  const termMetaValue = urlParams['term_meta[]'];

  return buildQueryDataUrl({
    query,
    rs,
    termMeta: typeof termMetaValue === 'string' ? [termMetaValue] : termMetaValue,
    guideAdded: { term, index },
  });
}
